import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export const UNIT_SALE_CLASSIFICATIONS = [
  'GENERAL',
  'MIDTERM',
  'STAY',
  'STAFF',
  'SHOWROOM',
  'SHORT_TERM',
  'FEMALE_ONLY',
  'MALE_ONLY',
] as const;

export type UnitSaleClassification = (typeof UNIT_SALE_CLASSIFICATIONS)[number];

export interface UnitSaleClassificationView {
  target_kind: 'UNIT';
  target_id: string;
  revision: number;
  fingerprint: string | null;
  room_topology_revision: number;
  value: UnitSaleClassification | null;
}

export interface UnitSaleClassificationCommand {
  command_id: string;
  expected_revision: number;
  expected_fingerprint: string | null;
  room_topology_revision: number;
  classification: UnitSaleClassification;
}

export interface UnitSaleClassificationReceipt {
  target_kind: 'UNIT';
  target_id: string;
  result_revision: number;
  content_fingerprint: string;
  value: UnitSaleClassification;
  outcome: 'CHANGED' | 'NO_CHANGE';
  http_status: 200;
}

type RecordValue = Record<string, unknown>;
const isRecord = (value: unknown): value is RecordValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const hasExactKeys = (value: RecordValue, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);
const isClassification = (value: unknown): value is UnitSaleClassification =>
  typeof value === 'string' && (UNIT_SALE_CLASSIFICATIONS as readonly string[]).includes(value);
const isFingerprint = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f]{64}$/i.test(value);
const isSafeRevision = (value: unknown, allowZero: boolean) =>
  Number.isSafeInteger(value) && Number(value) >= (allowZero ? 0 : 1) && Number(value) < 2 ** 63;
const sameUuid = (value: unknown, expected: string) =>
  isUuid(value) && value.toLowerCase() === expected.toLowerCase();

const invalid = (status?: number) =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    status,
    message: 'Unit 판매 분류 응답을 확인하지 못했습니다.',
  });

function parseView(value: unknown, unitId: string): UnitSaleClassificationView {
  const view = value;
  if (
    !isRecord(view) ||
    !hasExactKeys(view, [
      'target_kind',
      'target_id',
      'revision',
      'fingerprint',
      'room_topology_revision',
      'value',
    ]) ||
    view.target_kind !== 'UNIT' ||
    !sameUuid(view.target_id, unitId) ||
    !isSafeRevision(view.revision, true) ||
    !isSafeRevision(view.room_topology_revision, false) ||
    !(view.fingerprint === null || isFingerprint(view.fingerprint)) ||
    !(view.value === null || isClassification(view.value)) ||
    (view.revision === 0
      ? view.fingerprint !== null || view.value !== null
      : view.fingerprint === null)
  )
    throw invalid(200);
  return view as unknown as UnitSaleClassificationView;
}

function validateCommand(unitId: string, command: UnitSaleClassificationCommand) {
  if (
    !isUuid(unitId) ||
    !isRecord(command) ||
    !hasExactKeys(command, [
      'command_id',
      'expected_revision',
      'expected_fingerprint',
      'room_topology_revision',
      'classification',
    ]) ||
    !isUuid(command.command_id) ||
    !isSafeRevision(command.expected_revision, true) ||
    !(command.expected_fingerprint === null || isFingerprint(command.expected_fingerprint)) ||
    (command.expected_revision === 0
      ? command.expected_fingerprint !== null
      : command.expected_fingerprint === null) ||
    !isSafeRevision(command.room_topology_revision, false) ||
    !isClassification(command.classification)
  )
    throw invalid();
}

function parseReceipt(
  value: unknown,
  unitId: string,
  command: UnitSaleClassificationCommand,
): UnitSaleClassificationReceipt {
  const receipt = value;
  if (
    !isRecord(receipt) ||
    !hasExactKeys(receipt, [
      'target_kind',
      'target_id',
      'result_revision',
      'content_fingerprint',
      'value',
      'outcome',
      'http_status',
    ]) ||
    receipt.target_kind !== 'UNIT' ||
    !sameUuid(receipt.target_id, unitId) ||
    !isSafeRevision(receipt.result_revision, false) ||
    !isFingerprint(receipt.content_fingerprint) ||
    receipt.value !== command.classification ||
    (receipt.outcome !== 'CHANGED' && receipt.outcome !== 'NO_CHANGE') ||
    (receipt.outcome === 'CHANGED' && receipt.result_revision !== command.expected_revision + 1) ||
    (receipt.outcome === 'NO_CHANGE' && receipt.result_revision !== command.expected_revision) ||
    receipt.http_status !== 200
  )
    throw invalid(200);
  return receipt as unknown as UnitSaleClassificationReceipt;
}

/** Read the current state for one actual Core Unit UUID. */
export async function readUnitSaleClassification(
  client: ServiceClient,
  unitId: string,
  signal?: AbortSignal,
): Promise<UnitSaleClassificationView> {
  if (!isUuid(unitId)) throw invalid();
  const response = await client.get<unknown>(`admin/units/${unitId}/sale-classification`, {
    preserveEnvelope: true,
    signal,
  });
  if (!isRecord(response) || !hasExactKeys(response, ['data'])) throw invalid(200);
  return parseView(response.data, unitId);
}

/** Send an immutable one-Unit command; callers retain the same key/body after an unknown result. */
export async function setUnitSaleClassification(
  client: ServiceClient,
  unitId: string,
  command: UnitSaleClassificationCommand,
): Promise<UnitSaleClassificationReceipt> {
  validateCommand(unitId, command);
  const response = await client.put<unknown>(`admin/units/${unitId}/sale-classification`, {
    json: command,
    preserveEnvelope: true,
  });
  if (!isRecord(response) || !hasExactKeys(response, ['data'])) throw invalid(200);
  return parseReceipt(response.data, unitId, command);
}
